/**
 * OrdersList — listagem de CockpitOrders com filtros por estado.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import OrderStatusBadge from './components/OrderStatusBadge.jsx';
import { useTableProfile } from '../../hooks/useTableProfile.js';
import { DataTable } from '@shared/UI/DataTable';
import '@shared/UI/DataTable/DataTable.css';

const STATUS_FILTERS = [
  { value: '', label: 'Todos' },
  { value: '1', label: 'Rascunho' },
  { value: '6', label: 'Disponível' },
  { value: '3', label: 'Enviada SAP' },
  { value: '4', label: 'Finalizada' },
  { value: '8', label: 'Aguarda Hospitalar' },
  { value: '9', label: 'Liberada' },
  { value: '10', label: 'Aguarda Exceção' },
  { value: '11', label: 'Revisão Cliente' },
  { value: '5', label: 'Cancelada' },
];

const eur = (n) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(n || 0);
const date = (s) => s ? new Date(s).toLocaleDateString('pt-PT') : '—';

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status_id: '', search: '', date_from: '', date_to: '', my_orders: false,
  });
  const { layoutConfig, saveLayout } = useTableProfile('gencrm_orders_list');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const r = await api.get('/orders', { params });
      setOrders(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const columns = useMemo(() => [
    { key: 'order_number', label: 'Nº', width: 110, render: (o) => <Link to={`/cockpit/orders/${o.id}`}>#{o.order_number}</Link> },
    { key: 'customer_name', label: 'Cliente', width: 220, render: (o) => o.customer_name || '—' },
    { key: 'order_date', label: 'Data', width: 120, render: (o) => date(o.order_date) },
    { key: 'order_type', label: 'Tipo', width: 90, render: (o) => <code>{o.order_type}</code> },
    { key: 'status_id', label: 'Estado', width: 140, render: (o) => <OrderStatusBadge statusId={o.status_id} size="sm" /> },
    { key: 'total_price', label: 'Total', width: 120, aggregate: 'sum', render: (o) => eur(o.total_price) },
    { key: 'sap_order_number', label: 'SAP VBELN', width: 130, render: (o) => o.sap_order_number || '—' },
    { key: 'line_count', label: 'Linhas', width: 80 },
  ], []);

  return (
    <div className="gencrm_cockpit_orderslist">
      <div className="gencrm_cockpit_orderslist__filters">
        <select
          className="gencrm_app__filter-input"
          value={filters.status_id}
          onChange={(e) => setFilters({ ...filters, status_id: e.target.value })}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>Estado: {f.label}</option>
          ))}
        </select>
        <input
          className="gencrm_app__filter-input"
          placeholder="Pesquisar (nº, cliente, SAP)"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        />
        <input
          type="date"
          className="gencrm_app__filter-input"
          value={filters.date_from}
          onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
        />
        <input
          type="date"
          className="gencrm_app__filter-input"
          value={filters.date_to}
          onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
        />
        <label className="gencrm_filter-checkbox">
          <input
            type="checkbox"
            checked={filters.my_orders}
            onChange={(e) => setFilters({ ...filters, my_orders: e.target.checked })}
          /> Minhas
        </label>
        <button className="gencrm_btn gencrm_btn--primary" onClick={load}>Aplicar</button>
      </div>

      {error && <div className="gencrm_ws_error">Erro: {error}</div>}

      <DataTable
        columns={columns}
        data={orders}
        rowKey={(o) => o.id}
        loading={loading}
        pageSize={20}
        density="compact"
        enableTotals
        emptyText="Sem encomendas."
        exportPrefix="gencrm_orders"
        layoutConfig={layoutConfig}
        onLayoutChange={saveLayout}
      />
    </div>
  );
};

export default OrdersList;
